import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useIsMobile } from '../hooks/useIsMobile'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import { api } from '../services/api'
import { colors } from '../styles/tokens'

const SCORE_COLORS = { nao_abriu: '#cbd5e1', frio: '#94a3b8', morno: '#eab308', quente: '#22c55e' }
const SCORE_LABELS = { nao_abriu: 'Não abriu', frio: 'Frio', morno: 'Morno', quente: 'Quente' }

function formatTime(s) {
  if (!s) return '0s'
  return s < 60 ? `${Math.round(s)}s` : `${Math.round(s / 60)}m`
}

export default function EngagementPanel() {
  const { profile } = useAuth()
  const isMobile = useIsMobile()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [editVal, setEditVal] = useState('')

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    try { setData(await api.getEngagementDashboard()) }
    catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  const save = async (name) => {
    try {
      await api.updateEngagementThreshold(name, parseInt(editVal))
      setEditing(null)
      loadData()
    } catch (e) { alert('Erro: ' + e.message) }
  }

  const conv = data?.convertidos || {}
  const perd = data?.perdidos || {}
  const total = data?.total_outcomes || 0
  const byScore = data?.conversion_by_score || {}

  return (
    <div style={{ display: 'flex', height: isMobile ? 'auto' : '100vh', minHeight: '100vh', backgroundColor: colors.background }}>
      <Sidebar activePage="engajamento" />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Header subtitle="Engajamento" userName={profile?.name || 'Usuário'} />
        <main style={{ flex: 1, overflow: 'auto', padding: isMobile ? '1rem' : '2rem', paddingBottom: isMobile ? '80px' : '2rem' }}>
          <h2 style={{ margin: '0 0 1.5rem', fontSize: '1.5rem', fontWeight: 700, color: colors.text.primary }}>
            Calibração de Engajamento
          </h2>

          {loading ? <p style={{ color: colors.text.secondary }}>Carregando...</p>
          : !data ? <p style={{ color: colors.text.secondary }}>Erro ao carregar.</p>
          : total < 5 ? (
            <div style={s.card}>
              <p style={{ color: colors.text.secondary, margin: 0 }}>
                Ainda sem dados suficientes. São necessários pelo menos 5 desfechos (ganho/perdido).
              </p>
              <p style={{ color: colors.text.muted, margin: '0.5rem 0 0', fontSize: '0.875rem' }}>
                Registrados: {total}
              </p>
            </div>
          ) : (
            <>
              {/* Comparação lado a lado */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <CompCard title="Convertidas" count={conv.count} avgTime={conv.avg_time} avgVisits={conv.avg_visits} dist={conv.score_distribution} color="#10b981" icon="✓" />
                <CompCard title="Perdidas" count={perd.count} avgTime={perd.avg_time} avgVisits={perd.avg_visits} dist={perd.score_distribution} color="#ef4444" icon="✗" />
              </div>

              {/* Conversão por score */}
              {Object.keys(byScore).length > 0 && (
                <div style={{ ...s.card, marginBottom: '1.5rem' }}>
                  <h3 style={s.section}>Taxa de Conversão por Classificação</h3>
                  <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                    {['quente', 'morno', 'frio'].map(sc => {
                      const rate = byScore[sc]
                      if (rate === undefined) return null
                      return (
                        <div key={sc} style={{ textAlign: 'center' }}>
                          <div style={{
                            width: 60, height: 60, borderRadius: '50%',
                            backgroundColor: SCORE_COLORS[sc] + '20', border: `3px solid ${SCORE_COLORS[sc]}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 0.5rem', fontSize: '1.1rem', fontWeight: 700, color: SCORE_COLORS[sc]
                          }}>{rate}%</div>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{SCORE_LABELS[sc]}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Thresholds */}
              <div style={s.card}>
                <h3 style={s.section}>Parâmetros da Matriz</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {(data.thresholds || []).map(t => (
                    <div key={t.param_name} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.75rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb'
                    }}>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{t.description || t.param_name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Limites: {t.min_allowed} – {t.max_allowed}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {editing === t.param_name ? (
                          <>
                            <input type="number" value={editVal} onChange={e => setEditVal(e.target.value)}
                              min={t.min_allowed} max={t.max_allowed}
                              style={{ width: 70, padding: '0.25rem 0.5rem', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.875rem', textAlign: 'center' }} />
                            <button onClick={() => save(t.param_name)} style={{ ...s.btn, backgroundColor: '#10b981', color: 'white' }}>Salvar</button>
                            <button onClick={() => setEditing(null)} style={{ ...s.btn, backgroundColor: '#f3f4f6', color: '#6b7280' }}>Cancelar</button>
                          </>
                        ) : (
                          <>
                            <span style={{ fontWeight: 700, fontSize: '1.1rem', color: colors.primary, minWidth: 40, textAlign: 'center' }}>{t.param_value}</span>
                            <button onClick={() => { setEditing(t.param_name); setEditVal(String(t.param_value)) }}
                              style={{ ...s.btn, backgroundColor: '#f3f4f6', color: '#374151' }}>Editar</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  )
}

function CompCard({ title, count, avgTime, avgVisits, dist, color, icon }) {
  return (
    <div style={{ ...s.card, borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <span style={{ fontSize: '1.2rem' }}>{icon}</span>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{title}</h3>
        <span style={{ marginLeft: 'auto', fontSize: '1.5rem', fontWeight: 700, color }}>{count || 0}</span>
      </div>
      <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem' }}>
        <div><div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Tempo médio</div><div style={{ fontWeight: 600 }}>{formatTime(avgTime)}</div></div>
        <div><div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Visitas médias</div><div style={{ fontWeight: 600 }}>{avgVisits || 0}</div></div>
      </div>
      {dist && (
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {['quente', 'morno', 'frio', 'nao_abriu'].map(sc => {
            const v = dist[sc] || 0
            if (!v) return null
            return (
              <div key={sc} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: SCORE_COLORS[sc] }} />
                <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{SCORE_LABELS[sc]}: {v}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const s = {
  card: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1.25rem' },
  section: { margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: '#0f172a' },
  btn: { padding: '0.3rem 0.75rem', borderRadius: 6, border: 'none', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer' }
}
